import { useCallback, useContext, useMemo, createContext, useReducer } from 'react';

// Actions
import {
    UPDATE_DATE,
    UPDATE_DATE_PERIOD,
    UPDATE_PERIOD,
    UPDATE_COMPARE_TO,
    UPDATE_MIN_DATE,
    TOGGLE
} from '@/utils/hooks/datepicker/actions';

// Types
import type {
    DatepickerContextValueType,
    DatepickerDataType,
    DatepickerProviderType,
    DatepickerReducerActionsType
} from '@/hooks/datepicker/types';

const DatepickerReducer = (state: DatepickerDataType, action: DatepickerReducerActionsType) => {
    switch (action.type) {
        case UPDATE_DATE: {
            return {
                ...state,
                date: action.payload
            };
        }
        case UPDATE_DATE_PERIOD: {
            return {
                ...state,
                date: action.payload.date,
                periodSelected: action.payload.period
            };
        }
        case UPDATE_PERIOD: {
            return {
                ...state,
                periodSelected: action.payload
            };
        }
        case UPDATE_COMPARE_TO: {
            return {
                ...state,
                compareTo: action.payload
            };
        }
        case UPDATE_MIN_DATE: {
            return {
                ...state,
                options: {
                    ...state.options,
                    minDate: action.payload
                }
            };
        }
        case TOGGLE: {
            return {
                ...state,
                isOpen: !state.isOpen
            };
        }
    }
};

const DatepickerContext = createContext<DatepickerContextValueType | undefined>(undefined);

const DatepickerProvider = ({ defaultValues, children }: DatepickerProviderType) => {
    const [state, dispatch] = useReducer(DatepickerReducer, {
        ...defaultValues,
        options: {
            placement: 'bottom-start',
            ...defaultValues.options
        }
    });

    const dispatchGlobalInfo = useCallback(
        (args: DatepickerReducerActionsType) => dispatch(args),
        [dispatch]
    );
    const datepickerData = useMemo(() => state, [state]);
    return (
        <DatepickerContext.Provider value={{ datepickerData, dispatch: dispatchGlobalInfo }}>
            {children}
        </DatepickerContext.Provider>
    );
};

const useDatepicker = () => {
    const context = useContext(DatepickerContext);
    if (!context) {
        throw new Error('useDatepicker must be used within a GlobalInfoProvider');
    }
    return context;
};

export { DatepickerProvider, useDatepicker };
